import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import ErrorBoundary from "@/components/ErrorBoundary";
import InstallPrompt from "@/components/InstallPrompt";
import UpgradeModal from "@/components/UpgradeModal";
import CookieBanner from "@/components/CookieBanner";
import Landing from "@/pages/Landing";
import AuthCallback from "@/pages/AuthCallback";
import AppShell from "@/pages/AppShell";
import AccessGate from "@/pages/AccessGate";
import Dashboard from "@/pages/Dashboard";
import Receipts from "@/pages/Receipts";
import Bank from "@/pages/Bank";
import Grants from "@/pages/Grants";
import Statements from "@/pages/Statements";
import Accountants from "@/pages/Accountants";
import Settings from "@/pages/Settings";
import Advisor from "@/pages/Advisor";
import Audit from "@/pages/Audit";
import Privacy from "@/pages/legal/Privacy";
import Terms from "@/pages/legal/Terms";
import DPIA from "@/pages/legal/DPIA";
import Certificates from "@/pages/Certificates";
import PublicCertificate from "@/pages/PublicCertificate";
import EmbedCertificate from "@/pages/EmbedCertificate";
import Integrations from "@/pages/Integrations";
import Compatibility from "@/pages/Compatibility";
import IntegrationVendor from "@/pages/IntegrationVendor";
import Analytics from "@/pages/Analytics";
import Currency from "@/pages/Currency";
import Invoices from "@/pages/Invoices";
import TrustCenter from "@/pages/TrustCenter";
import Status from "@/pages/Status";
import Documents from "@/pages/Documents";
import Compliance from "@/pages/compliance/Compliance";
import TaxReliefs from "@/pages/tax/TaxReliefs";
import Payroll from "@/pages/Payroll";
import SetupWizard from "@/pages/SetupWizard";
import Billing from "@/pages/Billing";
import Runway from "@/pages/Runway";
import CapTable from "@/pages/CapTable";
import DataRoom from "@/pages/DataRoom";
import DataRoomShared from "@/pages/DataRoomShared";
import AccountantPortal from "@/pages/AccountantPortal";
import Admin from "@/pages/Admin";
import Team from "@/pages/Team";
import HandoutWeb from "@/pages/handouts/HandoutWeb";
import HandoutMobile from "@/pages/handouts/HandoutMobile";
import WhyPercen360 from "@/pages/handouts/WhyPercen360";

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (location.state?.user) return children;
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center aurora">
        <div className="glass rounded-2xl px-10 py-8 text-center">
          <div className="eyebrow mb-3">PerCen360</div>
          <div className="text-lg font-display">Verifying session…</div>
          <div className="mt-4"><span className="dot-flash"/><span className="dot-flash"/><span className="dot-flash"/></div>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  // Pre-launch gate: signed-in users must redeem an invite code (admins bypass).
  if (!user.access_granted && !user.is_admin) return <AccessGate />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Handle OAuth callback synchronously to prevent race conditions
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/why" element={<WhyPercen360 />} />
      <Route path="/handout/web" element={<HandoutWeb />} />
      <Route path="/handout/mobile" element={<HandoutMobile />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/dpia" element={<DPIA />} />
      <Route path="/compatibility" element={<Compatibility />} />
      <Route path="/integrations/:vendor" element={<IntegrationVendor />} />
      <Route path="/certificate/:token" element={<PublicCertificate />} />
      <Route path="/embed/certificate/:token" element={<EmbedCertificate />} />
      <Route path="/trust/:slug" element={<TrustCenter />} />
      <Route path="/status" element={<Status />} />
      <Route path="/data-room/:token" element={<DataRoomShared />} />
      <Route element={<Protected><AppShell/></Protected>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/receipts" element={<Receipts />} />
        <Route path="/bank" element={<Bank />} />
        <Route path="/grants" element={<Grants />} />
        <Route path="/certificates" element={<Certificates />} />
        <Route path="/integrations" element={<Integrations />} />
        <Route path="/statements" element={<Statements />} />
        <Route path="/documents" element={<Documents />} />
        <Route path="/compliance" element={<Compliance />} />
        <Route path="/tax-reliefs" element={<TaxReliefs />} />
        <Route path="/payroll" element={<Payroll />} />
        <Route path="/setup" element={<SetupWizard />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/runway" element={<Runway />} />
        <Route path="/captable" element={<CapTable />} />
        <Route path="/dataroom" element={<DataRoom />} />
        <Route path="/portal" element={<AccountantPortal />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/currency" element={<Currency />} />
        <Route path="/invoices" element={<Invoices />} />
        <Route path="/accountants" element={<Accountants />} />
        <Route path="/team" element={<Team />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/advisor" element={<Advisor />} />
        <Route path="/audit" element={<Audit />} />
        <Route path="/admin" element={<Admin />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace/>} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <AuthProvider>
          <AppRouter />
          <InstallPrompt />
          <UpgradeModal />
          <CookieBanner />
        </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}
